/*eslint-disable no-eval*/
import _ from "lodash";
import { IEntityValueDeviceModel, IEntityValueTagModel, IEntityValueTankModel, IEntityValueWellModel } from "../../common/models/IEntityValueModels";
import { ITankBattery } from "../../common/models/ITankBattery";
import { ObjectFeaturesEnum } from "../../common/enums/ObjectFeaturesEnum";
import i18n from "../../services/localization-service/i18n";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { OIL_TANK_CATEGORY_ID, UNGROUPED_CATEGORY_ID, WATER_TANK_CATEGORY_ID, WELL_DEVICE_CATEGORY_ID } from "../../components/object-tag-values/ObjectTagValuesUtils";
import { IKeyValuePair } from "../../common/models/KeyValuePair";
import { ITankStyle } from "../../common/models/TankStyle";
import { ILookupList } from "../../common/models/ILookupList";
import { ITagValue } from "../../common/models/ITagValue";
import { LiquidProductionTATEnum } from "../../common/enums/LiquidProductionTATEnum";
import { getTimeframeTimeDisplayText } from "../../common/utilities/TimeframeUtilities";
import { TimeframeTypeEnum } from "../../common/enums/TimeframeTypeEnum";
import { ALL_TAGS_CATEGORY_ID, ITagCategory } from "../../common/models/ITagCategory";
import Enumerable from "linq";
import { getTotalFromFeetAndInchesText } from "../../common/utilities/FeetAndInches";
import { SetPointEventTypeEnum } from "../../common/enums/SetPointEventTypeEnum";
import { getHoursMinutesSecondsText } from "../../common/utilities/HoursMinutesSeconds";


export type ManualEntryFormRefType = {
    getChanges: () => ITagValue[];
    getTags: () => IEntityValueTagModel[];
};


export type ManualEntryFormStateType = {
    isDirty: boolean;
    isValid: boolean;
}

export enum ManualEntryFormMode {
    ManualEntry = 0,
    WriteDown = 1
}

export type TagFormType = {
    id: number;
    name: string;
    tatID: number;
    //tatName?: string;
    //tatDescription?: string;
    displayText?: string;
    //color?: string;
    decimalPlaces: number;
    lowerBound?: number;
    writeLowerBound?: number;
    lowerBoundOp?: string;
    writeLowerBoundOp?: string;
    upperBound?: number;
    writeUpperBound?: number;
    upperBoundOp?: string;
    writeUpperBoundOp?: string;
    
    uomID?: number;
    //unitOfMeasureID?: number;

    unitDisplayName: string;
    //unit?: string;
    listID?: number;
    tagValue?: ITagValue;
    features: ObjectFeaturesEnum;

    categoryID?: number;
    categoryName?: string;
    categoryOrder?: number;

    contractHour: string;

    // optional for UI
    lookupList?: ILookupList;

    tankStyle?: ITankStyle;

    value: number | undefined | null;
    timestamp: string | undefined;

    objectID: number | undefined,
    objectTypeID: eLynxObjectTypeEnum | undefined

    feetAndInchesText?: string;

    order: number | undefined;
};


export type ManualEntryFormType = {
    id: number,
    tags: TagFormType[]
};

export type TagFormDirtyType = {
    value?: boolean
    feetAndInchesText?: boolean
}

export type ManualEntryFormDirtyType = {
    tags?: TagFormDirtyType[]
}


export const defaultManualEntryForm: ManualEntryFormType = {
    id: 0,
    tags: []
}

function _groupByCategory (tags: TagFormType[],
    categories: ITagCategory[]
): IKeyValuePair<string, TagFormType[]>[]  {

    let data = tags.reduce((result: IKeyValuePair<string, TagFormType[]>[], o) => {

        const { categoryName } = o;

        const item = result.find(v => v.Key === categoryName);

        if (item === undefined) {
            result.push({
                Key: categoryName as string,
                Value: [o]
            });
        } else {
            item.Value.push(o);
        }

        return result;
    },
    []);

    data.forEach(o => {
        o.Value = o.Value.sort((a, b) => {
            if (a.tatID ===  LiquidProductionTATEnum.TopLevel ||
                                        a.tatID === LiquidProductionTATEnum.InterfaceLevel ||
                                        a.tatID === LiquidProductionTATEnum.OilHaul ||
                                        a.tatID === LiquidProductionTATEnum.WaterHaul ||
                                        b.tatID ===  LiquidProductionTATEnum.TopLevel ||
                                        b.tatID === LiquidProductionTATEnum.InterfaceLevel ||
                                        b.tatID === LiquidProductionTATEnum.OilHaul ||
                                        b.tatID === LiquidProductionTATEnum.WaterHaul) {

                return (a.order ?? 0) - (b.order ?? 0);
            }
            
            return (a.name?.toLowerCase() ?? '').localeCompare((b.name.toLowerCase() ?? ''))
        });
    });

    if (categories.length && categories.find(o => o.id !== ALL_TAGS_CATEGORY_ID)) {

        data = Enumerable.from(data)
                .join(categories,
                    o => o.Key,
                    v => v.text,
                    o => o)
                .toArray();
    }

    return data;
}



const ManualEntryFormUtils = {

    mapToForm: function(mode: ManualEntryFormMode,
        model: IEntityValueDeviceModel | IEntityValueTankModel | IEntityValueWellModel | ITankBattery | undefined,
        offline: boolean,
        viewAllTags?: boolean
        ): ManualEntryFormType {

        const form: ManualEntryFormType = {
            id: 0,
            tags: []
        }

        //clone the initial model so that any changes made to the data will not affect the original object
        const clonedModel = _.cloneDeep(model)

        let hasTankStyle = false;

        let tags = viewAllTags ? (clonedModel?.tags ?? []) : clonedModel?.tags.filter(o => o.features && 
                        (o.features & (mode === ManualEntryFormMode.ManualEntry ? 
                                ObjectFeaturesEnum.ManualEntry : 
                                ObjectFeaturesEnum.Writedown)) > 0) ?? [];

        tags.forEach(o => {

            const category = clonedModel?.categories?.find(v => (v.id === o.categoryID && o.categoryID) || v.text === o.categoryName);

            o.categoryName = category?.text ?? i18n.t('tag.ungrouped') as string;
            o.categoryOrder = category?.order ?? UNGROUPED_CATEGORY_ID;
        });

        switch (clonedModel?.objectType) {
            case eLynxObjectTypeEnum.Device: {
                const device = clonedModel as IEntityValueDeviceModel;
                hasTankStyle = !!device.tankStyle;
                if (device.tanks) {

                    device.tanks.forEach((o) => {

                        const tank = o as IEntityValueTankModel;

                        const tankTags = tank?.tags.filter(o => o.features && 
                                        (o.features & (mode === ManualEntryFormMode.ManualEntry ? 
                                                ObjectFeaturesEnum.ManualEntry : 
                                                ObjectFeaturesEnum.Writedown)) > 0) ?? [];

                        tankTags.forEach( o => {
                            o.categoryName = tank.displayText;
                            o.categoryOrder = tank.subTypeID === 1 ? OIL_TANK_CATEGORY_ID : WATER_TANK_CATEGORY_ID;
                            o.tankStyle = tank.tankStyle;
                            o.order = o.tatID === LiquidProductionTATEnum.TopLevel ? 
                                                        100 :
                                        o.tatID === LiquidProductionTATEnum.InterfaceLevel ?
                                                        101 :
                                        o.tatID === LiquidProductionTATEnum.OilHaul ?
                                                        102 :
                                        o.tatID === LiquidProductionTATEnum.WaterHaul ?
                                                        103 :
                                        undefined;
                        });

                        tags = tags.concat(tankTags);
                    });
                }

                break;
            }

            case eLynxObjectTypeEnum.Well: {
                const well = clonedModel as IEntityValueWellModel;
                if (well.devices) {

                    well.devices.forEach((o) => {

                        const device = o as IEntityValueDeviceModel;

                        const deviceTags = device?.tags.filter(o => o.features && 
                                            (o.features & (mode === ManualEntryFormMode.ManualEntry ? 
                                                    ObjectFeaturesEnum.ManualEntry : 
                                                    ObjectFeaturesEnum.Writedown)) > 0) ?? [];
                        deviceTags.forEach( o => {
                            o.categoryName = device.displayText;
                            o.categoryOrder = WELL_DEVICE_CATEGORY_ID;
                        });
                        
                        tags = tags.concat(deviceTags);
                    });
                }

                break;
            }

            case eLynxObjectTypeEnum.TankBattery: {
                const tankBattery = clonedModel as ITankBattery;
                if (offline && tankBattery.tanks) {

                    tankBattery.tanks.forEach((tank) => {

                        const tankTags = tank?.tags.filter(o => o.features && 
                                            (o.features & (mode === ManualEntryFormMode.ManualEntry ? 
                                                    ObjectFeaturesEnum.ManualEntry : 
                                                    ObjectFeaturesEnum.Writedown)) > 0) ?? [];

                        tankTags.forEach( o => {
                            o.categoryName = tank.displayText;
                            o.categoryOrder = tank.subTypeID === 1 ? OIL_TANK_CATEGORY_ID : WATER_TANK_CATEGORY_ID;
                            o.tankStyle = tankBattery.tankStyles.find(v => v.id === tank.tankStyleID);
                            o.order = o.tatID === LiquidProductionTATEnum.TopLevel ? 
                                                        100 :
                                        o.tatID === LiquidProductionTATEnum.InterfaceLevel ?
                                                        101 :
                                        o.tatID === LiquidProductionTATEnum.OilHaul ?
                                                        102 :
                                        o.tatID === LiquidProductionTATEnum.WaterHaul ?
                                                        103 :
                                        undefined;

                        });

                        tags = tags.concat(tankTags);
                    });
                }

                break;
            }
        }

        form.tags = tags.sort((a, b) => {

            if (a.categoryOrder === OIL_TANK_CATEGORY_ID || a.categoryID === WATER_TANK_CATEGORY_ID ||
                b.categoryOrder === OIL_TANK_CATEGORY_ID || b.categoryID === WATER_TANK_CATEGORY_ID
            ) {
                return ((a.categoryID ?? 0) + (a.order ?? 0)) - ((b.categoryID ?? 0) + (b.order ?? 0));
            }
            
            return (a.categoryOrder ?? 0) - (b.categoryOrder?? 0) || 
                                        (a.categoryName ?? '').localeCompare((b.categoryName ?? '')) ||
                                        (a.order === undefined ? (a.displayText ?? '').localeCompare((b.displayText ?? '')) :
                                        ((a.order ?? 0)) - ((b.order ?? 0)))

            }).map(o => {



            const timestampString =  o.tagValue?.timestamp ? getTimeframeTimeDisplayText(TimeframeTypeEnum.None,  o.tagValue?.timestamp) : '';

            const formTag = {
                id: o.id,
                name: o.displayText,
                tatID: o.tatID as number,
                decimalPlaces: o.decimalPlaces ?? 0,
                lowerBound: o.lowerBound, 
                writeLowerBound: o.writeLowerBound,
                lowerBoundOp: o.lowerBoundOp,
                writeLowerBoundOp: o.writeLowerBoundOp,
                upperBound: o.upperBound,
                writeUpperBound: o.writeUpperBound,
                upperBoundOp: o.upperBoundOp,
                writeUpperBoundOp: o.writeUpperBoundOp,
                
                uomID: o.uomID,
                //unitOfMeasureID?: number;
            
                unitDisplayName: o.unitDisplayName,
                //unit?: string;
                listID: o.listID,
                tagValue: o.tagValue,
                features: o.features,
            
                categoryID: o.categoryID,
                categoryName: o.categoryName,
                categoryOrder: o.categoryOrder,
            
                contractHour: o.contractHour,
            
                lookupList: o.lookupList,
            
                tankStyle: o.tankStyle,

                value: o.lookupList ? 
                                ((o.tagValue?.value === undefined || o.tagValue?.value === null) ? -1 : o.tagValue?.value) : 
                        (o.tagValue?.value !== undefined && o.tagValue?.value !== null) ? 
                            parseFloat(o.tagValue.value.toString()).toFixed(o.decimalPlaces ?? 0) : 
                            null,
                timestamp: timestampString,
                objectID: o.tagObject?.id,
                objectTypeID: o.tagObject?.objectType,


                // feetandinchestext is for using the masked input field in the UI
                feetAndInchesText: ((o.features ?? ObjectFeaturesEnum.None) & ObjectFeaturesEnum.FeetAndInches) > 0 && 
                                        !hasTankStyle ? 
                                        //getFeetAndInchesText(o.tagValue?.value) : 
                                        o.tagValue?.displayText ?? '' :
                                        undefined,
                order: o.order
                 
            } as TagFormType;


            return formTag;
        });


        const groupedTanks = _groupByCategory(form.tags.filter(o => o.categoryOrder === OIL_TANK_CATEGORY_ID || o.categoryOrder === WATER_TANK_CATEGORY_ID), []);

        groupedTanks.forEach(o => {
            const tags = o.Value;

            const topLevel = tags.find(v => v.tatID === LiquidProductionTATEnum.TopLevel);
            const interfaceLevel = tags.find(v => v.tatID === LiquidProductionTATEnum.InterfaceLevel);
            const waterHaul = tags.find(v => v.tatID === LiquidProductionTATEnum.WaterHaul);
            if (topLevel && topLevel.value !== undefined && interfaceLevel && interfaceLevel.value === undefined) {

                interfaceLevel.value = 0;

                if (waterHaul && waterHaul.value === undefined) {
                    waterHaul.value = 0;
                }
            }
        })

        return form;
    },
    groupByCategory: (tags: TagFormType[], 
        categories: ITagCategory[],
        filterText: string) 
        : IKeyValuePair<string, TagFormType[]>[] => {

        const filteredCategories = categories.filter(o => o.selected || o.id === ALL_TAGS_CATEGORY_ID);
        const filteredTags = tags.filter(o => (o.name && o.name.toLowerCase().includes(filterText.trim().toLowerCase())) || filterText.trim() === '');
        return _groupByCategory(filteredTags, filteredCategories).filter(o => o.Value.length > 0);
    },
    validateFeetAndInches: (value: string,  item: TagFormType): boolean | string => {
    
        const newValue = value.replace(/_/g, ' ').trim();

        const parts = newValue.split("'");
        if (parts.length > 2) {
            return "Invalid feet and inches format.";
        }

        const feet = parts[0].trim();
        let inches = parts[1].replace(/ /g, '').replace('"', '');
        if (inches === '') {
            inches = '0';
        }

        if ((+inches) > 11.99 || isNaN(+feet) || isNaN(+inches)) {
            return "Invalid feet and inches format.";
        }

        if ((item.writeLowerBound !== undefined && item.writeLowerBound !== null && item.writeLowerBoundOp) || 
                (item.writeUpperBound !== undefined && item.writeUpperBound !== null && item.writeUpperBoundOp)) {

            // bounds check
            let rawValue = getTotalFromFeetAndInchesText(`${feet}' ${(+(inches)).toFixed(2)}"`);
            if (rawValue && item.unitDisplayName?.toLowerCase() === 'feet') {
                rawValue /= 12;
            }

            if (item.writeLowerBound !== undefined && item.writeLowerBoundOp !== undefined) {
                if (!(0, eval)(`${rawValue} ${item.writeLowerBoundOp} ${item.writeLowerBound}`)) {
                    return `The value must be ${item.writeLowerBoundOp} ${item.writeLowerBound}`;
                }
            }
    
            if (item.writeUpperBound !== undefined && item.writeUpperBoundOp !== undefined) {
                if (!(0, eval)(`${rawValue} ${item.writeUpperBoundOp} ${item.writeUpperBound}`)) {
                    return `The value must be ${item.writeUpperBoundOp} ${item.writeUpperBound}`;
                }
            }
        }


        return true
    
    },
     validateGasAnalysisInput: (gasAnalysisTags: TagFormType[],  tolerance: number): boolean | string => {
    
     
    
            const totalPercent: number = Enumerable.from(gasAnalysisTags).sum(o => +(o.value ?? 0));
    
            const maxDecimals = Enumerable.from(gasAnalysisTags).max(o => o.decimalPlaces ?? 0);
            const roundedValue = _.round(Math.abs(100 - totalPercent), maxDecimals);
    
            const invalid = roundedValue > tolerance;
    
            return invalid ? `The sum of all gas composition tags for this device must equal 100% +-(${tolerance}).` : true;
    },
    validateBounds: (mode: ManualEntryFormMode, value: number | undefined, tag: TagFormType): boolean | string => {
    
        if (mode === ManualEntryFormMode.WriteDown && (value === undefined || isNaN(value)) && tag.tagValue?.value !== undefined ) {
            return `A value is required for write down`
        }

        if (value !== undefined && !isNaN(value) && tag.writeLowerBound !== undefined && tag.writeLowerBoundOp) {

            if (!(0, eval)(`${value} ${tag.writeLowerBoundOp} ${tag.writeLowerBound}`)) {
                return `The value must be ${tag.writeLowerBoundOp} ${tag.writeLowerBound}`;
            }
        } 
        
        if (value !== undefined && !isNaN(value) && tag.writeUpperBound !== undefined && tag.writeUpperBoundOp) {

            if (!(0, eval)(`${value} ${tag.writeUpperBoundOp} ${tag.writeUpperBound}`)) {
                return `The value must be ${tag.writeUpperBoundOp} ${tag.writeUpperBound}`;
            }
        }

        return true;
    },

    validateTankTopLevel: (tags: TagFormType[], value: number, tag: TagFormType): boolean | string => {

        //const topLevelIndex = tags.findIndex(o => o.id === tag.id);


        const interfaceLevel = tags.findIndex(o => o.tatID === LiquidProductionTATEnum.InterfaceLevel && 
                                    o.objectID === tag.objectID && 
                                    o.objectTypeID === tag.objectTypeID);


        if (interfaceLevel !== undefined && !isNaN(value) && +interfaceLevel > +value) {
            return `The top level must be greater than or equal to the interface level`;
        }

        // clearErrors(`tags.${topLevelIndex}.value`);
    
        return true;
    },

    validateInterfaceLevelOpen: (tags: TagFormType[], value: number, tag: TagFormType): boolean | string =>{

    
        const topLevel = tags.find(o => o.tatID === LiquidProductionTATEnum.TopLevel && 
                                    o.objectID === tag.objectID && 
                                    o.objectTypeID === tag.objectTypeID);


        if (topLevel !== undefined && !isNaN(value) && +topLevel < +value) {
            return `The interface level must be less than or equal to the top level`;
        }

        //clearErrors(`tags.${interfaceLevelIndex}.value`);
    
        return true;
    },
    getModifiedValues: (date: Date | undefined, dirtyFields: ManualEntryFormDirtyType, tags: TagFormType[]) =>{

        const modifiedDate = date ?? new Date();

        const timeStamp =  new Date(modifiedDate.getFullYear(),
        modifiedDate.getMonth(),
        modifiedDate.getDate());

        const timestampString = timeStamp.toISOString();

        const newValues: ITagValue[] = [];

        const modifiedFields = dirtyFields as ManualEntryFormDirtyType;
    
        modifiedFields.tags?.forEach((x, ndx) => {

            if (x?.value) {
                const formTag = tags[ndx];            

                if (formTag.feetAndInchesText) {

                    const feetAndInchesValue = formTag.feetAndInchesText?.replace(/_/g, '0').trim() ?? '';
                    const parts = feetAndInchesValue.split("'");

                    const feet = parts[0].replace(/ /g, '');
                    const inches = parts[1]?.replace('"', '').replace(/ |_/g, '') ?? '0';

                    const feetAndInches = `${feet}' ${(+(inches)).toFixed(2)}"`;

                    let value = getTotalFromFeetAndInchesText(feetAndInches);
                    if (formTag.unitDisplayName.toLowerCase() === 'feet') {
                        value! /= 12;
                    }

                    const newTagValue: ITagValue = {
                        value: value,
                        displayText: feetAndInches,
                        setPointEventType: SetPointEventTypeEnum.Manual,
                        tagID: formTag.id,
                        unitOfMeasureID: formTag.uomID,
                        unitDisplayName: formTag.unitDisplayName,
                        timestamp: timestampString,
                    };

                    newValues.push(newTagValue);

                } else {

                    const value: number | undefined = formTag.value === null ? undefined : formTag.value; 
                    let displayText: string | undefined = formTag.value?.toString();

                    if (formTag.features && (formTag.features & ObjectFeaturesEnum.HoursMinutesSeconds) > 0) {
                        displayText = getHoursMinutesSecondsText(value)
                    }

                    const newTagValue: ITagValue = {
                        value: value,
                        displayText: displayText,
                        setPointEventType: SetPointEventTypeEnum.Manual,
                        tagID: formTag.id,
                        unitOfMeasureID: formTag.uomID,
                        unitDisplayName: formTag.unitDisplayName,
                        timestamp: timestampString,
                    };

                    newValues.push(newTagValue);
                }
            }
        });

        return newValues;
    }


}

export default ManualEntryFormUtils;